import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, CalendarDays, MapPin, Radio, Gavel, Trophy, Newspaper, Star } from "lucide-react";
import api from "@/lib/api";
import PollCard from "@/components/PollCard";
import Countdown from "@/components/Countdown";

const HERO_IMG = "/asl-hero.jpg";
const FANS_IMG = "https://images.unsplash.com/photo-1762013315117-1c8005ad2b41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxleGNpdGVkJTIwc3BvcnRzJTIwZmFucyUyMHN0YWRpdW18ZW58MHx8fHwxNzg4MTk0OTkzfDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.22, 1, 0.36, 1];

function RevealLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-league-deep" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="Cricket match in progress" className="w-full h-full object-cover scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-league-deep via-league-blue/40 to-league-deep/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap items-center gap-3 mb-6"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-[11px] font-bold uppercase tracking-[0.2em]" data-testid="hero-season-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-league-red animate-pulse" />
            Season 6 — Registrations Open
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
          className="mb-7"
        >
          <motion.span
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="inline-block drop-shadow-2xl"
          >
            <img src="/asl-logo-white.png" alt="All Star League crest" className="h-28 sm:h-32 w-auto" data-testid="hero-crest" />
          </motion.span>
        </motion.div>

        <h1 className="font-display text-white leading-[0.9] tracking-tighter" data-testid="hero-headline">
          <RevealLine delay={0.35} className="text-6xl sm:text-7xl lg:text-[9rem]">ALL STAR</RevealLine>
          <RevealLine delay={0.5} className="text-6xl sm:text-7xl lg:text-[9rem]">
            LEAGUE <span className="text-league-red">6.0</span>
          </RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-6 max-w-xl text-white/80 text-base md:text-lg leading-relaxed"
          data-testid="hero-subtext"
        >
          Surat's biggest cricket carnival returns this February. Seven franchises. One trophy.
          Every ball, every bid, every roar — right here.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/news"
            data-testid="hero-cta-news"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-league-blue font-bold text-sm uppercase tracking-wider transition-colors duration-200 hover:bg-league-red hover:text-white"
          >
            Latest Updates
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/polls"
            data-testid="hero-cta-polls"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/70 text-white font-bold text-sm uppercase tracking-wider transition-colors duration-200 hover:bg-white hover:text-league-blue"
          >
            Vote in Fan Polls
          </Link>
          <div className="flex items-center gap-2 text-white/70 text-sm font-semibold">
            <MapPin size={15} className="text-league-red" /> Surat
            <span className="mx-1 text-white/30">/</span>
            <CalendarDays size={15} className="text-league-red" /> 19–21 Feb 2027
          </div>
        </motion.div>

        <Countdown />
      </motion.div>
    </section>
  );
}

function EditorialMarquee({ announcements }) {
  const items = announcements.length
    ? announcements.map((a) => a.text)
    : ["ALL STAR LEAGUE 6.0", "SURAT — FEBRUARY 2027", "DATES DROPPING SOON"];
  return (
    <div className="bg-league-blue py-4 border-y-4 border-league-ink overflow-hidden" data-testid="announcement-marquee">
      <Marquee speed={35} gradient={false}>
        {items.map((t, i) => (
          <span key={i} className="mx-6 font-display text-xl sm:text-2xl tracking-wide whitespace-nowrap">
            <span className={i % 2 ? "text-outline-white" : "text-white"}>{t}</span>
            <span className="text-league-red mx-6">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

const CHAPTERS = [
  { num: "01", title: "BORN IN SURAT", body: "What started as a neighbourhood tournament is now the city's loudest sporting tradition — five seasons, thousands of fans, countless last-ball finishes." },
  { num: "02", title: "BUILT BY COMMUNITY", body: "Polls, fan parks, live streams and a gallery full of memories. This league belongs to the people in the stands as much as the players on the pitch." },
  { num: "03", title: "BACK FOR 6.0", body: "February 2027. New franchises, a mega auction, live match streams and the biggest prize purse yet. The sixth edition changes everything." },
];

function Manifesto() {
  return (
    <section className="py-24 sm:py-32 bg-league-mist" data-testid="manifesto-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
        <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue">The League Manifesto</span>
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.num}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.05, ease: EASE }}
            className="mt-16 sm:mt-24"
            data-testid={`manifesto-chapter-${c.num}`}
          >
            <span className="font-oswald text-league-red font-semibold tracking-[0.3em] text-sm">{c.num}</span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-league-ink tracking-tighter mt-3">{c.title}</h2>
            <p className="mt-5 text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">{c.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function NewsPreview({ news }) {
  if (!news.length) return null;
  const featured = news.find((n) => n.featured) || news[0];
  const rest = news.filter((n) => n.id !== featured.id).slice(0, 3);
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="news-preview-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue flex items-center gap-2">
              <Newspaper size={14} /> League Wire
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-league-ink tracking-tighter mt-3">NEWS & UPDATES</h2>
          </div>
          <Link to="/news" data-testid="news-view-all" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-league-blue transition-colors hover:text-league-red">
            All stories <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, ease: EASE }}
            className="md:col-span-2 md:row-span-2 group"
            data-testid={`news-featured-${featured.id}`}
          >
            <Link to="/news" className="block">
              <div className="overflow-hidden rounded-xl border border-black/5">
                <img src={featured.image_url} alt={featured.title} className="w-full h-72 sm:h-96 object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <span className="inline-block mt-5 px-3 py-1 rounded-full bg-league-blue/10 text-league-blue text-[11px] font-bold uppercase tracking-widest">{featured.tag}</span>
              <h3 className="font-oswald text-2xl md:text-3xl font-semibold text-league-ink mt-3 leading-tight transition-colors duration-200 group-hover:text-league-blue">{featured.title}</h3>
              <p className="text-muted-foreground mt-3 leading-relaxed line-clamp-3">{featured.body}</p>
            </Link>
          </motion.article>
          {rest.map((n, i) => (
            <motion.article
              key={n.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: 0.1 + i * 0.08, ease: EASE }}
              className="group"
              data-testid={`news-card-${n.id}`}
            >
              <Link to="/news" className="block">
                <div className="overflow-hidden rounded-xl border border-black/5">
                  <img src={n.image_url} alt={n.title} className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <span className="inline-block mt-4 px-3 py-1 rounded-full bg-league-blue/10 text-league-blue text-[11px] font-bold uppercase tracking-widest">{n.tag}</span>
                <h3 className="font-oswald text-lg font-semibold text-league-ink mt-2 leading-snug transition-colors duration-200 group-hover:text-league-blue">{n.title}</h3>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function WinnersPreview({ winners }) {
  if (!winners.length) return null;
  return (
    <section className="py-24 sm:py-32 bg-league-ink text-white" data-testid="winners-preview-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-red flex items-center gap-2">
              <Trophy size={14} /> Hall of Fame
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3">PAST CHAMPIONS</h2>
          </div>
          <Link to="/winners" data-testid="winners-view-all" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white/80 transition-colors hover:text-league-red">
            Full board <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="border-t border-white/10">
          {winners.slice(0, 5).map((w, i) => (
            <motion.div
              key={w.id}
              initial={{ opacity: 0, x: -32 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.06, ease: EASE }}
              className="grid grid-cols-12 items-center gap-3 py-5 border-b border-white/10 group"
              data-testid={`winner-row-${w.season}`}
            >
              <span className="col-span-2 font-oswald text-3xl sm:text-4xl font-semibold text-white/25 transition-colors duration-200 group-hover:text-league-red">
                S{w.season}.0
              </span>
              <span className="col-span-5 sm:col-span-4 font-oswald text-lg sm:text-2xl font-semibold tracking-wide">
                {w.champion}
                <span className="block text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-league-red mt-1">Champions</span>
              </span>
              <span className="col-span-5 sm:col-span-4 font-oswald text-lg sm:text-2xl font-semibold tracking-wide">
                {w.runner_up}
                <span className="block text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-white/40 mt-1">Runners-up</span>
              </span>
              <span className="hidden sm:block sm:col-span-2 text-right font-oswald text-white/60">{w.year}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PollsPreview({ polls }) {
  if (!polls.length) return null;
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="polls-preview-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-12">
          <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue">Your Voice Counts</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-league-ink tracking-tighter mt-3">FAN POLLS</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {polls.slice(0, 3).map((p, i) => (
            <PollCard key={p.id} poll={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ComingSoonTeasers() {
  const cards = [
    { to: "/live", icon: Radio, title: "LIVE MATCHES", body: "Ball-by-ball streams of every ASL 6.0 fixture, right here on matchday.", testid: "teaser-live" },
    { to: "/auction", icon: Gavel, title: "LIVE AUCTION", body: "Watch franchises go to war for 180+ players in the mega auction stream.", testid: "teaser-auction" },
  ];
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="coming-soon-section">
      <img src={FANS_IMG} alt="Stadium fans" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-league-deep/70" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter mb-12">
          COMING <span className="text-outline-white">THIS FEBRUARY</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {cards.map((c, i) => (
            <motion.div
              key={c.to}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
            >
              <Link
                to={c.to}
                data-testid={c.testid}
                className="block backdrop-blur-xl bg-white/70 saturate-150 border border-white rounded-2xl p-8 sm:p-10 hover:-translate-y-1 transition-transform duration-300"
              >
                <span className="inline-flex w-12 h-12 rounded-full bg-league-blue text-white items-center justify-center mb-6">
                  <c.icon size={22} />
                </span>
                <h3 className="font-display text-3xl sm:text-4xl text-league-ink tracking-tight">{c.title}</h3>
                <p className="mt-3 text-league-ink/70 leading-relaxed">{c.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-league-blue">
                  Get notified <ArrowRight size={15} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PERFORMERS = [
  {
    name: "Kanak Beewal", team: "Blue Blasters", photo: "/player-kanak-beewal.jpg",
    awards: [{ label: "Player of the Series", color: "#b45309" }],
    stat: "140 runs @ 200 SR • 13 wickets",
    mvp: true,
  },
  {
    name: "Aman Prajapati", team: "Blue Blasters", photo: "/player-aman-prajapati.jpg",
    awards: [{ label: "Orange Cap", color: "#ea580c" }, { label: "Most 6s", color: "#9f1239" }],
    stat: "317 runs • 31 sixes • 205.8 SR",
  },
  {
    name: "Mitesh Gupta", team: "Lovkesh Warriors", photo: "/player-mitesh-gupta.jpg",
    awards: [{ label: "Purple Cap", color: "#6d28d9" }],
    stat: "15 wickets • 20.0 overs • 8.50 econ",
  },
  {
    name: "Lokesh Jangir", team: "Lovkesh Warriors", photo: "/player-lokesh-jangir.jpg",
    awards: [{ label: "Most 4s", color: "#be185d" }],
    stat: "243 runs • 26 fours • 166.4 SR",
  },
  {
    name: "Saurabh Agarwal", team: "Lovkesh Warriors", photo: "/player-saurabh-agarwal.jpg",
    awards: [{ label: "Most Catches", color: "#4338ca" }],
    stat: "9 catches • 9 caught behind",
  },
  {
    name: "Neet Savaliya", team: "The Rapture Boys", photo: "/player-neet-savaliya.jpg",
    awards: [{ label: "Most Dots Bowled", color: "#b91c1c" }],
    stat: "63 dot balls • 9 wickets • 7.81 econ",
  },
];

function TopPerformers() {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="top-performers-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-blue flex items-center gap-2">
              <Star size={14} /> Season 5.0 Honours
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-league-ink tracking-tighter mt-3">TOP PERFORMERS</h2>
          </div>
          <span className="hidden sm:block text-xs text-muted-foreground font-semibold uppercase tracking-widest">Stats via CricHeroes</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PERFORMERS.map((p, i) => (
            <motion.div
              key={p.name + i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
              className={`group bg-league-mist border rounded-2xl overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(0,74,173,0.3)] transition-[transform,box-shadow] duration-300 ${p.mvp ? "border-amber-400 ring-2 ring-amber-400/60" : "border-black/5"}`}
              data-testid={`performer-card-${i}`}
            >
              <div className="relative overflow-hidden">
                <img src={p.photo} alt={p.name} className="w-full h-60 object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  {p.awards.map((a) => (
                    <span key={a.label} className="px-3 py-1 rounded-full text-white text-[10px] font-bold uppercase tracking-widest shadow-lg" style={{ backgroundColor: a.color }}>
                      {a.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-oswald text-xl font-semibold text-league-ink leading-tight">{p.name}</h3>
                <p className="text-xs font-bold uppercase tracking-widest text-league-blue mt-1">{p.team}</p>
                <p className="font-oswald text-sm text-muted-foreground mt-3 pt-3 border-t border-black/5">{p.stat}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [news, setNews] = useState([]);
  const [winners, setWinners] = useState([]);
  const [polls, setPolls] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/winners").then((r) => setWinners(r.data)).catch(() => {});
    api.get("/polls").then((r) => setPolls(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee announcements={announcements} />
      <Manifesto />
      <NewsPreview news={news} />
      <WinnersPreview winners={winners} />
      <TopPerformers />
      <PollsPreview polls={polls} />
      <ComingSoonTeasers />
    </main>
  );
}
